import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AnalysisSessionCard from './components/AnalysisSessionCard';
import StatisticsOverview from './components/StatisticsOverview';
import FilterControls from './components/FilterControls';

import Icon from '../../components/AppIcon';
import EmptyState from '../../components/ui/EmptyState';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { getSignedThumbnailUrl } from '../../utils/thumbnailCompression';
import BottomNav from '../../components/navigation/BottomNav';
import { isAuthError } from '../../utils/isAuthError';
import Header from '../../components/navigation/Header';

const DashboardAnalysisHistory = () => {
  const navigate = useNavigate();
  const { user, userProfile, creditBalance, signOut, analysisHistory, refreshAnalysisHistory } = useAuth();
  const [sortBy, setSortBy] = useState('date');
  const [filterScore, setFilterScore] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [analysisSessions, setAnalysisSessions] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [deleteError, setDeleteError] = useState(null);

  const displayName = userProfile?.full_name || user?.user_metadata?.full_name || user?.email?.split('@')?.[0] || 'User';
  const displayEmail = user?.email || '';

  useEffect(() => {
    const loadHistory = async () => {
      if (!user?.id) {
        setIsLoadingHistory(false);
        return;
      }
      setIsLoadingHistory(true);
      try {
        const { data, error } = await supabase
          ?.from('analysis_history')
          ?.select('id, photo_count, average_score, highest_score, gender_filter, credits_used, created_at, results')
          ?.eq('user_id', user?.id)
          ?.order('created_at', { ascending: false })
          ?.limit(5);
        if (!error && data) {
          // Generate signed URLs for thumbnails stored in private bucket
          const mapped = await Promise.all(
            data?.map(async (session) => {
              const rankedPhotos = session?.results?.rankedPhotos?.slice(0, 3) || [];

              const thumbnails = await Promise.all(
                rankedPhotos?.map(async (p, i) => {
                  let imageUrl = '';
                  if (p?.thumbnailPath) {
                    try {
                      imageUrl = await getSignedThumbnailUrl(supabase, p?.thumbnailPath);
                    } catch (urlErr) {
                      console.warn(`Signed URL generation failed for thumbnail ${i + 1}:`, urlErr?.message);
                    }
                  }
                  return { image: imageUrl, alt: p?.imageAlt || `Ranked photo ${i + 1}` };
                })
              );

              return {
                id: session?.id,
                date: session?.created_at,
                photoCount: session?.photo_count,
                averageScore: parseFloat(session?.average_score) || 0,
                highestScore: parseFloat(session?.highest_score) || 0,
                thumbnails
              };
            })
          );
          setAnalysisSessions(mapped);
        }
      } catch (err) {
        console.error('Failed to load analysis history:', err);
      } finally {
        setIsLoadingHistory(false);
      }
    };
    loadHistory();
  }, [user?.id]);

  const getScoreColor = (score) => {
    if (score >= 8) return 'var(--color-success)';
    if (score >= 6) return 'var(--color-primary)';
    if (score >= 4) return 'var(--color-warning)';
    return 'var(--color-error)';
  };

  const filteredSessions = analysisSessions?.filter((session) => {
    if (filterScore === 'high' && session?.averageScore < 8) return false;
    if (filterScore === 'medium' && (session?.averageScore < 6 || session?.averageScore >= 8)) return false;
    if (filterScore === 'low' && session?.averageScore >= 6) return false;
    return true;
  });

  const sortedSessions = [...filteredSessions]?.sort((a, b) => {
    if (sortBy === 'date') return new Date(b?.date) - new Date(a?.date);
    if (sortBy === 'score') return b?.averageScore - a?.averageScore;
    if (sortBy === 'photos') return b?.photoCount - a?.photoCount;
    return 0;
  });

  const handleViewSession = (sessionId) => {
    const session = analysisSessions?.find(s => s?.id === sessionId);
    if (session) {
      navigate('/ranking-results-display', { state: { analysisResult: session?.results } });
    } else {
      navigate('/ranking-results-display');
    }
  };

  const handleDeleteSession = async (sessionId) => {
    // Auth check before attempting RLS-protected delete
    if (!user?.id) {
      setDeleteError('You must be signed in to delete history. Please sign in and try again.');
      navigate('/auth');
      return;
    }

    try {
      const { error } = await supabase?.from('analysis_history')?.delete()?.eq('id', sessionId);
      if (error) {
        if (isAuthError(error)) {
          // Session expired — do NOT ghost-delete from local state
          setDeleteError('Your session has expired. Please sign in again to delete history.');
          navigate('/auth');
          return;
        }
        setDeleteError(`Could not delete session: ${error?.message || 'Unknown error'}. Please try again.`);
        return;
      }
      // Only remove from local state after confirmed DB delete
      setAnalysisSessions(prev => prev?.filter(s => s?.id !== sessionId));
      setDeleteError(null);
    } catch (err) {
      console.error('Delete session error:', err);
      setDeleteError('An unexpected error occurred. Please try again.');
    }
  };

  const handleStartNewAnalysis = () => {
    navigate('/photo-upload-interface');
  };

  const handlePurchaseCredits = () => {
    navigate('/purchase-credits');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 via-white to-cyan-50 pb-24">
      <Header
        user={{ displayName, displayEmail, creditBalance }}
        onNavigate={(path) => navigate(path)}
        onPurchaseCredits={handlePurchaseCredits}
        onLogout={signOut}
      />
      <main className="main-content">
        <div className="max-w-6xl mx-auto px-4 py-8">
          {/* Header with decorative elements */}
          <div className="text-center mb-12 relative">
            {/* Decorative floating icons */}
            <div className="absolute -top-4 left-8 animate-float">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <Icon name="BarChart2" size={20} color="#9333EA" />
              </div>
            </div>
            <div className="absolute -top-4 right-8 animate-float-delayed">
              <Icon name="Sparkles" size={24} color="#06B6D4" />
            </div>
            
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text text-transparent mb-4">
              Dashboard & History
            </h1>
            <p className="text-lg text-muted-foreground">
              View your photo analysis history and insights
            </p>
          </div>

          {/* Delete error banner */}
          {deleteError && (
            <div className="mb-6 flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-700 text-sm">
              <Icon name="AlertCircle" size={18} className="mt-0.5 shrink-0" color="#DC2626" />
              <span>{deleteError}</span>
              <button onClick={() => setDeleteError(null)} className="ml-auto shrink-0 text-red-400 hover:text-red-600">
                <Icon name="X" size={16} color="currentColor" />
              </button>
            </div>
          )}

          {/* Premium AI Insights Promo Card */}
          <div
            onClick={() => navigate('/purchase-credits')}
            className="bg-gradient-to-br from-purple-600 via-pink-500 to-orange-500 rounded-2xl p-6 mb-8 cursor-pointer hover:shadow-2xl transition-all transform hover:scale-[1.01] relative overflow-hidden">
            
            <div className="absolute -right-12 -top-12 w-32 h-32 bg-white/10 rounded-full animate-float"></div>
            <div className="absolute -left-8 -bottom-8 w-24 h-24 bg-white/10 rounded-full animate-float-delayed"></div>
            <div className="relative">
              <div className="flex items-center gap-2 mb-3">
                <div className="bg-white/20 rounded-full px-3 py-1 text-white text-xs font-bold flex items-center gap-1">
                  <Icon name="Crown" size={12} color="#FFFFFF" />
                  PREMIUM FEATURE
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 flex-1">
                  <div className="bg-white/20 rounded-full p-3 animate-pulse">
                    <Icon name="Brain" size={32} color="#FFFFFF" />
                  </div>
                  <div className="text-left">
                    <h3 className="text-2xl font-bold text-white mb-1">
                      Unlock AI Profile Insights
                    </h3>
                    <p className="text-white/90">
                      Get personalized recommendations, trend analysis, and actionable tips to optimize your scores
                    </p>
                  </div>
                </div>
                <Icon name="ArrowRight" size={32} color="#FFFFFF" className="flex-shrink-0 ml-4" />
              </div>
            </div>
          </div>

          <StatisticsOverview sessions={analysisSessions} />

          <FilterControls
            sortBy={sortBy}
            onSortChange={setSortBy}
            filterScore={filterScore}
            onFilterChange={setFilterScore}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery} />

          {/* Last Five Sessions Header */}
          <h2 className="text-2xl font-bold text-foreground mb-6">
            Last Five Sessions
          </h2>

          {isLoadingHistory ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {[1, 2, 3]?.map((i) => (
                <div key={i} className="bg-white rounded-2xl shadow-md p-5 animate-pulse">
                  {/* Session header */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="space-y-1.5">
                      <div className="h-4 bg-gray-200 rounded w-28"></div>
                      <div className="h-3 bg-gray-100 rounded w-20"></div>
                    </div>
                    <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                  </div>
                  {/* Thumbnail strip */}
                  <div className="flex gap-2 mb-4">
                    {[1, 2, 3]?.map((j) => (
                      <div key={j} className="w-16 h-16 bg-gray-200 rounded-xl flex-1"></div>
                    ))}
                  </div>
                  {/* Score bar */}
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between">
                      <div className="h-3 bg-gray-200 rounded w-24"></div>
                      <div className="h-3 bg-gray-200 rounded w-10"></div>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full w-full">
                      <div className="h-2 bg-gray-200 rounded-full w-3/5"></div>
                    </div>
                  </div>
                  {/* Action buttons */}
                  <div className="flex gap-2 pt-2">
                    <div className="h-9 bg-gray-200 rounded-xl flex-1"></div>
                    <div className="h-9 bg-gray-200 rounded-xl w-9"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : sortedSessions?.length === 0 ? (
            <EmptyState
              icon="History"
              headline="No Analysis Sessions Found"
              explanation="Start your first photo ranking analysis to see results here. Upload 1-9 photos to discover your best angles."
              ctaText="Upload Photos"
              ctaIcon="Upload"
              onCtaClick={handleStartNewAnalysis} />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {sortedSessions?.slice(0, 5)?.map((session) =>
                <AnalysisSessionCard
                  key={session?.id}
                  session={session}
                  onView={handleViewSession}
                  onDelete={handleDeleteSession}
                  getScoreColor={getScoreColor} />
              )}
            </div>
          )}
        </div>
      </main>
      {/* Hidden file input for camera button */}
      <input
        id="camera-file-input-history"
        type="file"
        className="hidden"
        accept="image/jpeg,image/png,image/heic,image/jpg"
        multiple
      />

      <BottomNav activePath="/dashboard-analysis-history" cameraInputId="camera-file-input-history" />
    </div>
  );
};

export default DashboardAnalysisHistory;